import { useEffect, useState } from "react";
import axiosInstance from "../common/api";
import { Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Table } from "antd";
import { filterOption, requiredField } from "../common/common";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import SignItem from "./SignItem";
import LoadDpiSerials from "./LoadDpiSerials";
import UseDpiSerials from "./UseDpiSerials";

const ListItem = ({ field, form, products }) => {
  const [items, setItems] = useState([]);
  const dpiProduct = Form.useWatch(["lines", field.name, "productId"], form);
  const needSerial = products.filter((x) => x.id === dpiProduct)[0]?.dpi.trackingType === "SERIAL";

  useEffect(() => {
    form.setFieldValue(["lines", field.name, "inventoryUnit"], undefined);
    if (dpiProduct && needSerial) {
      axiosInstance
        .get("dpiInventoryUnit", { params: { dpiProduct, pageSize: 9999 } })
        .then(({ data }) => setItems(data.data));
    }
  }, [dpiProduct, needSerial, field.name, form]);

  return (
    <>
      <Form.Item
        style={{ margin: 0 }}
        name={[field.name, "inventoryUnit"]}
        hidden={!needSerial}
        rules={[requiredField]}
      >
        <Select
          showSearch
          filterOption={filterOption}
          options={items.map((x) => ({ label: x.serialNumber, value: x.id }))}
        />
      </Form.Item>
    </>
  );
};

export default function () {
  const navigate = useNavigate();
  const [signOpen, setSignOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form] = Form.useForm();
  const reasonWatch = Form.useWatch("reason", form);
  const signatureWatch = Form.useWatch("signature", form);
  const reason = reasons.filter((x) => x.id === reasonWatch)[0];
  const requiresEmployee = reasonWatch ? reason?.requiresEmployee : false;
  const requiresEmployeeSignature = reasonWatch ? reason?.requiresEmployeeSignature : false;
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (requiresEmployee && employees.length === 0)
      axiosInstance
        .get("employee", { params: { pageSize: 99999 } })
        .then(({ data }) => setEmployees(data.data));
  }, [requiresEmployee, employees]);

  useEffect(() => {
    axiosInstance.get("dpiTransactionReason").then(({ data }) => setReasons(data));
    axiosInstance
      .get("dpiProduct", { params: { pageSize: 9999 } })
      .then(({ data }) => setProducts(data.data));
  }, []);

  const onFormFinish = (formData) => {
    formData = { ...formData, date: formData.date.format("YYYY-MM-DD") };
    axiosInstance.post("dpiTransaction", formData).then(() => navigate(-1));
  };
  return (
    <Form
      id="newTransactionForm"
      form={form}
      disabled={!!signatureWatch}
      layout="vertical"
      colon={false}
      initialValues={{ lines: [{ sku: null, amount: null }] }}
      onFinish={onFormFinish}
    >
      <span
        style={{
          fontWeight: "bold",
          fontSize: "1.2em",
          marginBottom: 16,
          display: "block",
        }}
      >
        Testata
      </span>
      <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
        <div>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="date" rules={[requiredField]} label="Data">
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reason" rules={[requiredField]} label="Causale">
                <Select
                  options={reasons.map((x) => ({ label: x.reason, value: x.id }))}
                  showSearch
                  filterOption={filterOption}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              {requiresEmployee && (
                <Form.Item name="employee" rules={[requiredField]} label="Dipendente">
                  <Select
                    options={employees.map((x) => ({
                      label: x.fullName,
                      value: x.id,
                    }))}
                    showSearch
                    filterOption={filterOption}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item name="notes" label="Note">
            <Input.TextArea />
          </Form.Item>
        </div>

        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  paddingBottom: 16,
                  paddingTop: 48,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontWeight: "bold",
                    fontSize: "1.2em",
                  }}
                >
                  Righe
                </span>
                <Button icon={<PlusOutlined />} onClick={() => add()} style={{ width: 150 }}>
                  Nuova riga
                </Button>
              </div>
              <Table
                dataSource={fields}
                bordered
                rowKey={(x) => x.name}
                tableLayout="fixed"
                pagination={false}
                columns={[
                  {
                    key: "sku",
                    title: "Prodotto",
                    width: "40%",
                    render: (field) => (
                      <Form.Item
                        style={{ margin: 0 }}
                        name={[field.name, "productId"]}
                        rules={[requiredField]}
                      >
                        <Select
                          showSearch
                          variant="borderless"
                          filterOption={filterOption}
                          options={products.map((x) => ({ label: x.description, value: x.id }))}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "amount",
                    title: "Quantità",
                    render: (field) => (
                      <Form.Item
                        wrapperCol={{ span: 24 }}
                        style={{ margin: 0 }}
                        name={[field.name, "amount"]}
                        rules={[requiredField]}
                      >
                        <InputNumber
                          variant="borderless"
                          controls={false}
                          style={{ width: "100%" }}
                          min={1}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    key: "action",
                    title: "",
                    render: (field) => {
                      return (
                        <Space>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) =>
                              reason?.createSerial ? (
                                <Form.Item
                                  name={[field.name, "newInventoryUnits"]}
                                  style={{ margin: 0 }}
                                >
                                  <LoadDpiSerials
                                    quantity={getFieldValue(["lines", field.name, "amount"])}
                                  />
                                </Form.Item>
                              ) : (
                                <Form.Item
                                  name={[field.name, "inventoryUnits"]}
                                  style={{ margin: 0 }}
                                >
                                  <UseDpiSerials
                                    reason={reason}
                                    product={getFieldValue(["lines", field.name, "productId"])}
                                    quantity={getFieldValue(["lines", field.name, "amount"])}
                                  />
                                </Form.Item>
                              )
                            }
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button danger type="link" onClick={() => remove(field.name)}>
                              Rimuovi
                            </Button>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </>
          )}
        </Form.List>

        {requiresEmployeeSignature && (
          <>
            <span
              style={{
                flex: 1,
                fontWeight: "bold",
                fontSize: "1.2em",
                display: "block",
                paddingBottom: 16,
                paddingTop: 48,
              }}
            >
              Firma
            </span>
            <Form.Item name={"signature"} label="Firma per ricevuta" rules={[requiredField]}>
              <SignItem />
            </Form.Item>
          </>
        )}

        <div style={{ display: "flex", placeContent: "end" }}>
          <Button htmlType="submit" disabled={false} type="primary">
            Salva
          </Button>
        </div>
      </div>
    </Form>
  );
}
